import { formatDateForDisplay, removeMinYear } from '../../../utils/datetime'
import { Item, ToolTypes } from '../../../types'
import { Settings } from '../../../hooks/useSettings/useSettings.type'

// Weak Items type used as excel export data has arbitrary keys
const filterExcelData = (excelData: any[], settings: Settings) => {
   const columnOptions = JSON.parse(settings.ColumnOptions)
   const isColumnPresent = (column: string) => columnOptions.actionsTable.includes(column);

   // Create a boolean map of columns we need based on the user's settings, if isColumnPresent returns false, the column will be excluded from export
   // Cart Quantity is always returned as it only exists for uncompleted export
   // Action Comment is always returned as it only exists for completed export
   const columnsToAssign = {
      'Item Name': isColumnPresent('Title'),
      Type: isColumnPresent('ToolType'),
      'Assigned to Kit': isColumnPresent('AssignedToKitName'),
      'Total Qty': isColumnPresent('Quantity'),
      'Status Qty': isColumnPresent('StatusQuantity'),
      'Reorder Level': isColumnPresent('ReorderLevelQuantity'),
      Status: isColumnPresent('StatusDesc'),
      'Loaned/Pending To': isColumnPresent('AssignedToUser'),
      'Item Detail': isColumnPresent('Description'),
      Manufacturer: isColumnPresent('Manufacturer'),
      'Model No.': isColumnPresent('ModelNumber'),
      'Serial No.': isColumnPresent('SerialNumber'),
      Barcode: isColumnPresent('Barcode'),
      Category: isColumnPresent('Category'),
      'Purchase Date': isColumnPresent('PurchaseDate'),
      'Purchase Price': isColumnPresent('PurchasePrice'),
      'Total Value': isColumnPresent('TotalValue'),
      'Warranty Date': isColumnPresent('WarrantyDate'),
      'Rental Item': isColumnPresent('RentalItem'),
      'Rental Start Date': isColumnPresent('RentalDate'),
      'Rental End Date': isColumnPresent('RentalReturnDate'),
      [settings.CustomRentalRate1Label]: isColumnPresent('CustomRentalRate1'),
      [settings.CustomRentalRate2Label]: isColumnPresent('CustomRentalRate2'),
      [settings.CustomRentalRate3Label]: isColumnPresent('CustomRentalRate3'),
      [settings.CustomRentalRate4Label]: isColumnPresent('CustomRentalRate4'),
      [settings.CustomDate1Label]: isColumnPresent('CustomDate1Value'),
      [settings.CustomDate2Label]: isColumnPresent('CustomDate2Value'),
      [settings.CustomText1Label]: isColumnPresent('CustomText1Value'),
      [settings.CustomText2Label]: isColumnPresent('CustomText2Value'),
      [settings.CustomURL1Label]: isColumnPresent('CustomURL1Value'),
      [settings.CustomURL2Label]: isColumnPresent('CustomURL2Value'),
      'WebSite URL': isColumnPresent('WebSiteURL'),
      'Owners URL': isColumnPresent('AttachmentUrl'),
      'Home Warehouse': isColumnPresent('DefaultLocation'),
      'Current/Pending Location': isColumnPresent('CurrentLocation'),
      'Last Audit Date': isColumnPresent('LastAuditDate'),
      'Last Audit Condition': isColumnPresent('LastAuditConditionDesc'),
      Vendor: isColumnPresent('Vendor'),
      'Created Date': isColumnPresent('CreatedDate'),
      'Item ID': isColumnPresent('ItemId'),
   }

   // Delete the keys we don't need from the original object containing all of the data
   excelData.forEach((originalObject) => {
      for (const [key, value] of Object.entries(columnsToAssign)) {
         if (!value) {
            delete originalObject[key]
         }
      }

      return originalObject
   });
   
   return excelData;
}

export const generateExcelData = (items: Item[], settings: Settings) => {
   const columnOptions = JSON.parse(settings.ColumnOptions)

   const allData = items.map((item) => ({
      'Item Name': item.Title,
      Type: ToolTypes[item.ToolType],
      'Assigned to Kit': item.AssignedToKitName,
      'Total Qty': item.Quantity,
      'Reorder Level': item.ReorderLevelQuantity,
      'Status Qty': item.StatusQuantity,
      Status: item.StatusDesc,
      'Loaned/Pending To': item.AssignedToUser,
      'Item Detail': item.Description,
      Manufacturer: item.Manufacturer,
      'Model No.': item.ModelNumber,
      'Serial No.': item.SerialNumber,
      Barcode: item.Barcode,
      'Action Comment': item.Comments,
      Category: item.Category,
      'Purchase Date': removeMinYear(formatDateForDisplay(item.PurchaseDate)),
      'Purchase Price': item.PurchasePrice,
      'Total Value': item.TotalValue,
      'Warranty Date': removeMinYear(formatDateForDisplay(item.WarrantyDate)),
      'Rental Item': item.RentalDate ? 'rented' : 'not rented',
      'Rental Start Date': removeMinYear(formatDateForDisplay(item.RentalDate)),
      'Rental End Date': removeMinYear(formatDateForDisplay(item.RentalReturnDate)),
      [settings.CustomRentalRate1Label]: settings.CustomRentalRate1Label
         ? item.CustomRentalRate1
         : '-',
      [settings.CustomRentalRate2Label]: settings.CustomRentalRate2Label
         ? item.CustomRentalRate2
         : '-',
      [settings.CustomRentalRate3Label]: settings.CustomRentalRate3Label
         ? item.CustomRentalRate3
         : '-',
      [settings.CustomRentalRate4Label]: settings.CustomRentalRate4Label
         ? item.CustomRentalRate4
         : '-',
      [settings.CustomDate1Label]: settings.CustomDate1Label
         ? removeMinYear(formatDateForDisplay(item.CustomDate1Value))
         : '-',
      [settings.CustomDate2Label]: settings.CustomDate2Label
         ? removeMinYear(formatDateForDisplay(item.CustomDate2Value))
         : '-',
      [settings.CustomText1Label]: settings.CustomText1Label
         ? item.CustomText1Value
         : '-',
      [settings.CustomText2Label]: settings.CustomText2Label
         ? item.CustomText2Value
         : '-',
      [settings.CustomURL1Label]: settings.CustomURL1Label
         ? item.CustomURL1Value
         : '-',
      [settings.CustomURL2Label]: settings.CustomURL2Label
         ? item.CustomURL2Value
         : '-',
      'WebSite URL': item.WebSiteURL,
      'Owners URL': item.AttachmentUrl,
      'Home Warehouse': item.DefaultLocation,
      'Current/Pending Location': item.CurrentLocation,
      'Last Audit Date': removeMinYear(formatDateForDisplay(item.LastAuditDate)),
      'Last Audit Condition': item.LastAuditConditionDesc,
      Vendor: item.Vendor,
      'Created Date': removeMinYear(formatDateForDisplay(item.CreatedDate)),
      'Item ID': item.ItemId,
   }))

   // If columnOptions.actionsTable is an emtpy array, the user has no column modifications and we should just return all data
   if (!columnOptions?.actionsTable?.length) return allData

   // Otherwise, filter the data according to user's table preferences
   return filterExcelData(allData, settings);
}

export const generateExcelDataForActionsCart = (
   items: Item[],
   settings: Settings
) => {
   const columnOptions = JSON.parse(settings.ColumnOptions)

   const allData = items.map((item) => ({
      'Item Name': item.Title,
      Type: ToolTypes[item.ToolType],
      'Assigned to Kit': item.AssignedToKitName,
      Quantity: item.quantityForCart,
      'Total Qty': item.Quantity,
      'Reorder Level': item.ReorderLevelQuantity,
      'Status Qty': item.StatusQuantity,
      Status: item.StatusDesc,
      'Loaned/Pending To': item.AssignedToUser,
      'Item Detail': item.Description,
      Manufacturer: item.Manufacturer,
      'Model No.': item.ModelNumber,
      'Serial No.': item.SerialNumber,
      Barcode: item.Barcode,
      Category: item.Category,
      'Purchase Date': removeMinYear(formatDateForDisplay(item.PurchaseDate)),
      'Purchase Price': item.PurchasePrice,
      'Total Value': item.TotalValue,
      'Warranty Date': removeMinYear(formatDateForDisplay(item.WarrantyDate)),
      'Rental Item': item.RentalDate ? 'rented' : 'not rented',
      'Rental Start Date': removeMinYear(formatDateForDisplay(item.RentalDate)),
      'Rental End Date': removeMinYear(formatDateForDisplay(item.RentalReturnDate)),
      [settings.CustomRentalRate1Label]: settings.CustomRentalRate1Label
         ? item.CustomRentalRate1
         : '-',
      [settings.CustomRentalRate2Label]: settings.CustomRentalRate2Label
         ? item.CustomRentalRate2
         : '-',
      [settings.CustomRentalRate3Label]: settings.CustomRentalRate3Label
         ? item.CustomRentalRate3
         : '-',
      [settings.CustomRentalRate4Label]: settings.CustomRentalRate4Label
         ? item.CustomRentalRate4
         : '-',
      [settings.CustomDate1Label]: settings.CustomDate1Label
         ? removeMinYear(formatDateForDisplay(item.CustomDate1Value))
         : '-',
      [settings.CustomDate2Label]: settings.CustomDate2Label
         ? removeMinYear(formatDateForDisplay(item.CustomDate2Value))
         : '-',
      [settings.CustomText1Label]: settings.CustomText1Label
         ? item.CustomText1Value
         : '-',
      [settings.CustomText2Label]: settings.CustomText2Label
         ? item.CustomText2Value
         : '-',
      [settings.CustomURL1Label]: settings.CustomURL1Label
         ? item.CustomURL1Value
         : '-',
      [settings.CustomURL2Label]: settings.CustomURL2Label
         ? item.CustomURL2Value
         : '-',
      'WebSite URL': item.WebSiteURL,
      'Owners URL': item.AttachmentUrl,
      'Home Warehouse': item.DefaultLocation,
      'Current/Pending Location': item.CurrentLocation,
      'Last Audit Date': removeMinYear(formatDateForDisplay(item.LastAuditDate)),
      'Last Audit Condition': item.LastAuditConditionDesc,
      Vendor: item.Vendor,
      'Created Date': removeMinYear(formatDateForDisplay(item.CreatedDate)),
      'Item ID': item.ItemId,
   }))

   // If columnOptions.actionsTable is an emtpy array, the user has no column modifications and we should just return all data
   if (!columnOptions?.actionsTable?.length) return allData

   // Otherwise, filter the data according to user's table preferences
   return filterExcelData(allData, settings);
}
